import { memo, MouseEvent, useMemo, useState } from 'react';
import {
  Avatar,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth, useSecurity, useUser } from '@dfl/react-security';
import { ACCOUNT_MENU, MenuItemType } from 'settings/account.menu';
import { logoutCurrentSession } from 'modules/notification/services/auth-session.service';

export const SLink = styled('a')(() => ({
  alignItems: 'center',
  padding: '6px 16px',
  textDecoration: 'none',
  color: 'inherit',

  '.MuiListItemIcon-root': {
    minWidth: '36px',
  },
}));

const Account = () => {
  const { t } = useTranslation('common');
  const { hasPermission } = useSecurity();
  const { logout } = useAuth();
  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const options = useMemo(() => {
    return ACCOUNT_MENU?.filter((menu) => {
      return menu?.permission?.length ? hasPermission(menu.permission) : true;
    })?.map((menu, index) => {
      if (menu.divider) return <Divider key={index} />;

      const menuItem = menu as MenuItemType;
      const Icon = menuItem.icon;
      const content = (
        <>
          <ListItemIcon>
            <Icon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary={<Typography variant='body1'>{t(menuItem.label)}</Typography>} />
        </>
      );
      if (menuItem.external) {
        return (
          <SLink key={index} href={menuItem.link} target='_blank' rel='noopener noreferrer'>
            {content}
          </SLink>
        );
      }
      return (
        <MenuItem
          component={Link}
          to={menuItem.link}
          key={menuItem.label}
          onClick={() => {
            setAnchorEl(null);
          }}
        >
          {content}
        </MenuItem>
      );
    });
  }, [hasPermission, t]);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await logoutCurrentSession();
    } catch (error) {
      console.error('Unable to revoke the backend session', error);
    } finally {
      await logout();
    }
  };

  return (
    <div data-tour='step-navbar-1'>
      <IconButton color='secondary' onClick={handleOpen} aria-label={t('profile')}>
        <Avatar sx={{ width: 40, height: 40 }}>
          <PersonIcon />
        </Avatar>
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} PaperProps={{ sx: { width: 300 } }}>
        <MenuItem disabled>
          <ListItemText primary={user?.fullName} secondary={user?.email} />
        </MenuItem>
        <Divider />
        {options}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText primary={t('logout')} />
        </MenuItem>
      </Menu>
    </div>
  );
};

export default memo(Account);
